import { useCallback, type PropsWithChildren } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import OfferCardPriceOverrideContext, { type OfferCardPriceRenderer } from 'contexts/OfferList/OfferCardPriceOverrideContext'
import { isLEHotel } from 'lib/offer/offerTypes'

export interface SimilarOfferQuote {
  pricing: App.PricingWithValue
  memberPricing?: App.PricingWithValue
  duration: number
  withFlights: boolean
  showValue: boolean
  hideDiscount?: boolean
}

export function SimilarOfferPricingProvider({ quotes, children }: PropsWithChildren<{ quotes: Record<string, SimilarOfferQuote | undefined> }>) {
  const renderPrice = useCallback<OfferCardPriceRenderer>(offer => {
    const quote = quotes[offer.id]
    return quote ? <SimilarOfferPricing quote={quote} offerConfig={isLEHotel(offer) ? offer.luxPlus : undefined} /> : undefined
  }, [quotes])
  return <OfferCardPriceOverrideContext.Provider value={renderPrice}>{children}</OfferCardPriceOverrideContext.Provider>
}

export default function SimilarOfferPricing({ quote, offerConfig }: { quote: SimilarOfferQuote, offerConfig?: App.HotelOffer['luxPlus'] }) {
  const intl = useIntl()
  const canViewMemberPrice = useAppSelector(getCanViewLuxPlusBenefits)
  const { pricing, memberPricing, duration, withFlights } = quote
  const displayed = canViewMemberPrice && memberPricing ? memberPricing : pricing
  const caption = withFlights ? intl.formatMessage({
    id: 'gEFgViLaxzHaUoh9q6IV',
    defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
    description: 'Bundle offer duration in nights with flights, shown as price caption on offer card',
  }, { count: duration }) : intl.formatMessage({
    id: 'bpRr7kqMdQCHkQ_UR10Z',
    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
    description: 'Package duration in nights with "from" suffix on offer card',
  }, { count: duration })
  const discount = !quote.hideDiscount && displayed.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? displayed.discountPercent : undefined
  return <Group direction="vertical" gap={4}>
    <PriceRowPriceCaption>{caption}</PriceRowPriceCaption>
    <LuxPlusPriceStack price={pricing.price} memberPrice={memberPricing?.price} size="L" saleUnit="total" offerConfig={offerConfig} testid="main-offer-pricing" />
    {quote.showValue && displayed.value > displayed.price && <PriceRowValueDiscountWithCaption size="L" originalValue={Math.ceil(displayed.value)} discountPercentage={discount} />}
  </Group>
}
